// ─── Group ───────────────────────────────────────────────────────────────────

export type GroupSlug = 'tsuyosa';

export interface Group {
  id: string;
  slug: GroupSlug;
  name: string;
  tagline: string;
  color_primary: string;
  color_secondary: string;
  cover_image_url: string;
  logo_url: string;
  created_at: string;
  updated_at: string;
}

// ─── Members ─────────────────────────────────────────────────────────────────

export type MemberType     = 'member' | 'trainee';
export type LeadershipRole = 'leader' | 'co-leader';
export type ActivityStatus = 'active' | 'graduated' | 'hiatus';

export type MemberPosition =
  | 'main_vocalist' | 'lead_vocalist' | 'vocalist'
  | 'main_dancer'   | 'lead_dancer'   | 'dancer'
  | 'main_rapper'   | 'lead_rapper'   | 'rapper'
  | 'visual' | 'center' | 'maknae';

export interface SocialLink {
  platform: string;
  handle: string | null;
  url: string;
}

export interface Member {
  id: string;
  slug: string;
  nickname: string;
  full_name: string | null;
  member_type: MemberType;
  leadership_role: LeadershipRole | null;
  activity_status: ActivityStatus;
  graduated_at: string | null;
  birth_date: string | null;
  height: number | null;
  blood_type: 'A' | 'B' | 'AB' | 'O' | null;
  positions: MemberPosition[];
  profile_photo: string | null;
  cover_photo: string | null;
  jikoshoukai: string | null;
  socials_json: SocialLink[];
  order_index: number;
  created_at: string;
  updated_at: string;
}

// ─── Discography ─────────────────────────────────────────────────────────────

export type ReleaseType   = 'single' | 'mini_album' | 'album' | 'digital' | 'collaboration';

export const RELEASE_TYPE_LABELS: Record<ReleaseType, string> = {
  single:        'Single',
  mini_album:    'Mini Album',
  album:         'Album',
  digital:       'Digital',
  collaboration: 'Collaboration',
};
export type DatePrecision = 'day' | 'month' | 'year';

export interface MemberRelease {
  release_id: string;
  member_id: string;
  role_in_release: string | null;
  sort_order: number;
  member?: Pick<Member, 'id' | 'slug' | 'nickname' | 'profile_photo'>;
}

export interface DiscographyRelease {
  id: string;
  slug: string;
  title: string;
  type: ReleaseType;
  cover_url: string | null;
  release_date: string | null;
  date_precision: DatePrecision;
  streaming_links: Record<string, string>;
  description: string | null;
  is_published: boolean;
  order_index: number;
  created_at: string;
  updated_at: string;
  members?: MemberRelease[];
}

// ─── News ────────────────────────────────────────────────────────────────────

export type NewsCategory =
  | 'BIRTHDAY' | 'NEW MEMBER' | 'EVENT' | 'GRADUATION'
  | 'RELEASE'  | 'CONCERT'   | 'ANNOUNCEMENT';

export interface NewsArticle {
  id: string;
  slug: string;
  date: string;
  category: NewsCategory;
  title: string;
  text: string;
  image_url?: string;
  content: string;
  is_featured: boolean;
  is_draft: boolean;
  created_at: string;
  updated_at: string;
}

// ─── Benefits ────────────────────────────────────────────────────────────────

export interface Benefit {
  id: string;
  title_id: string;
  title_jp: string;
  description_id?: string;
  description_jp?: string;
  icon_url?: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MarqueeItem {
  id: string;
  date: string;
  text: string;
  created_at: string;
}

// ─── Schedule ────────────────────────────────────────────────────────────────

export type ScheduleStatus = 'upcoming' | 'sold_out' | 'cancelled';

export interface ScheduleType {
  id: string;
  name_id: string;
  name_jp: string;
  sort_order: number;
  is_active: boolean;
}

export interface ScheduleMember {
  schedule_id: string;
  member_id: string;
  sort_order: number;
}

export interface Schedule {
  id: string;
  title: string;
  date: string;
  start_time?: string;
  end_time?: string;
  venue?: string;
  type_id?: string;
  type?: ScheduleType;
  status: ScheduleStatus;
  sales_start_at?: string;
  sales_end_at?: string;
  is_active: boolean;
  cheki_enabled: boolean;
  members?: ScheduleMember[];
  created_at: string;
  updated_at: string;
}

// ─── Cheki ───────────────────────────────────────────────────────────────────

export type ChekiType        = '2shot_solo' | 'all_member';
export type ChekiOrderStatus = 'pending' | 'paid' | 'done' | 'cancelled';

export interface ChekiPricing {
  id:             string;
  schedule_id:    string;
  type:           ChekiType;
  price_preorder: number;
  price_onspot:   number;
}

export interface ChekiOrder {
  id:          string;
  schedule_id: string;
  buyer_name:  string;
  email:       string;
  contact:     string | null;
  type:        ChekiType;
  member_id:   string | null;
  quantity:    number;
  total_price: number;
  status:      ChekiOrderStatus;
  notes:       string | null;
  created_at:  string;
  member?:     Pick<Member, 'id' | 'nickname'>;
  schedule?:   Pick<Schedule, 'id' | 'title' | 'date'>;
}

// ─── Assets ──────────────────────────────────────────────────────────────────

export type AssetFolder    = 'members' | 'news' | 'benefits' | 'discography';
export type AssetFeature   = 'member'  | 'news' | 'benefit'  | 'discography';
export type AssetFieldName = 'profile_photo' | 'cover_photo' | 'icon_url' | 'cover_url' | 'image_url';

export const ASSET_FOLDERS: readonly AssetFolder[] = ['members', 'news', 'benefits', 'discography'] as const;

export interface AssetUsage {
  feature:    AssetFeature;
  feature_id: string;
  label:      string;
}

export interface AssetItem {
  id:          string;
  file_id:     string;
  url:         string;
  folder:      AssetFolder;
  name:        string;
  size:        number;
  width:       number;
  height:      number;
  created_at:  string;
  usage_count: number;
  usages:      AssetUsage[];
}

// ─── Fan User ─────────────────────────────────────────────────────────────────

export interface FanUser {
  id:             string;
  email:          string;
  username:       string;
  display_name:   string | null;
  profile_photo:  string | null;
  bio:            string | null;
  is_active:      boolean;
  email_verified: boolean;
  created_at:     string;
  updated_at:     string;
}

// ─── Branded primitives ───────────────────────────────────────────────────────

export type AccessToken = string & { readonly _brand: 'AccessToken' };

// ─── API Response Wrappers ───────────────────────────────────────────────────

export interface PaginationMeta {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export interface ApiSuccess<T> {
  success: true;
  data: T;
  meta?: Partial<PaginationMeta>;
}

export interface ApiError {
  success: false;
  error: { code: string; message: string; details?: unknown };
}

export type ApiResponse<T> = ApiSuccess<T> | ApiError;
